import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import { getMaxUploadSizeByChannel } from '@chatwoot/utils';
import { DirectUpload } from 'activestorage';
import { setDirectUploadAuthHeaders } from 'dashboard/helper/directUploadsHelper';
import {
  DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE,
  resolveMaximumFileUploadSize,
} from 'shared/helpers/FileHelper';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

let uploadIdCounter = 0;
const nextUploadId = () => {
  uploadIdCounter += 1;
  return `upload-${Date.now()}-${uploadIdCounter}`;
};

export default {
  data() {
    return {
      // uploadId -> in-flight XHR, so cancelFileUpload() can abort it.
      activeUploadXHRs: {},
    };
  },

  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
    installationLimit() {
      return resolveMaximumFileUploadSize(
        this.globalConfig.maximumFileUploadSize
      );
    },
  },

  methods: {
    maxSizeFor(mime) {
      // Use default/installation limit for private notes
      if (this.isOnPrivateNote) {
        return this.installationLimit;
      }

      const channelType = this.inbox?.channel_type;

      if (!channelType || channelType === INBOX_TYPES.WEB) {
        return this.installationLimit;
      }

      const channelLimit = getMaxUploadSizeByChannel({
        channelType,
        medium: this.inbox?.medium, // e.g. 'sms' | 'whatsapp'
        mime, // e.g. 'image/png'
      });

      if (channelLimit === DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE) {
        return this.installationLimit;
      }

      return Math.min(channelLimit, this.installationLimit);
    },
    alertOverLimit(maxSizeMB) {
      useAlert(
        this.$t('CONVERSATION.FILE_SIZE_LIMIT', {
          MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE: maxSizeMB,
        })
      );
    },
    onFileUpload(file) {
      if (this.globalConfig.directUploadsEnabled) {
        this.onDirectFileUpload(file);
      } else {
        this.onIndirectFileUpload(file);
      }
    },

    /**
     * Performs the direct (ActiveStorage) upload for `file`, reporting
     * progress/success/failure via optional lifecycle hooks on the host
     * component (`onUploadStart`, `onUploadProgress`, `onUploadError`,
     * `onUploadSuccess`) - mirrors how `attachFile` is already called
     * unconditionally, trusting the host component to define it. Hooks are
     * optional (`?.`) so this stays backward compatible with any future
     * consumer that doesn't need progress UI.
     */
    onDirectFileUpload(file) {
      if (!file) return;

      const mime = file.file?.type || file.type;
      const maxSizeMB = this.maxSizeFor(mime);

      if (!checkFileSizeLimit(file, maxSizeMB)) {
        this.alertOverLimit(maxSizeMB);
        return;
      }

      const uploadId = file.uploadId || nextUploadId();
      // Stashed on the wrapper so retryFileUpload(file) can reuse the same
      // id after a failure, keeping the pending-list entry in place.
      file.uploadId = uploadId;

      this.onUploadStart?.({ file, uploadId });

      // Tracked as an object (not just the raw xhr) so cancelFileUpload -
      // called later, from a separate click handler outside this closure -
      // can flag the cancellation explicitly. Inferring "was this
      // cancelled?" purely from "is the xhr still in the map?" would wrongly
      // treat an upload that failed *before* directUploadWillStoreFileWithXHR
      // even fired (e.g. the initial blob-create request itself erroring)
      // as a cancellation too, swallowing a real error.
      const uploadHandle = { xhr: null, cancelled: false };
      this.activeUploadXHRs[uploadId] = uploadHandle;

      const upload = new DirectUpload(
        file.file,
        `/api/v1/accounts/${this.accountId}/conversations/${this.currentChat.id}/direct_uploads`,
        {
          directUploadWillCreateBlobWithXHR: xhr => {
            setDirectUploadAuthHeaders(xhr);
          },
          directUploadWillStoreFileWithXHR: xhr => {
            uploadHandle.xhr = xhr;
            xhr.upload.addEventListener('progress', event => {
              if (!event.lengthComputable) return;
              const progress = Math.round((event.loaded / event.total) * 100);
              this.onUploadProgress?.({ uploadId, progress });
            });
          },
        }
      );

      upload.create((error, blob) => {
        const { cancelled } = uploadHandle;
        delete this.activeUploadXHRs[uploadId];

        // A cancel requested before directUploadWillStoreFileWithXHR fired
        // (still in the small "create blob" request) has no xhr to abort
        // yet, so this callback can still complete normally afterwards -
        // guard both branches, not just the error one, so a "cancelled"
        // upload never silently attaches anyway.
        if (cancelled) return;

        if (error) {
          this.onUploadError?.({ file, uploadId, error });
          return;
        }

        this.attachFile({ file, blob });
        this.onUploadSuccess?.({ uploadId });
      });
    },

    /** Aborts an in-flight direct upload started via onDirectFileUpload. */
    cancelFileUpload(uploadId) {
      const handle = this.activeUploadXHRs[uploadId];
      if (!handle) return;
      handle.cancelled = true;
      handle.xhr?.abort();
    },

    /** Re-attempts a failed upload for the same file wrapper. */
    retryFileUpload(file) {
      this.onDirectFileUpload(file);
    },

    onIndirectFileUpload(file) {
      if (!file) return;

      const mime = file.file?.type || file.type;
      const maxSizeMB = this.maxSizeFor(mime);

      if (!checkFileSizeLimit(file, maxSizeMB)) {
        this.alertOverLimit(maxSizeMB);
        return;
      }

      this.attachFile({ file });
    },
  },
};
